import { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle2, Copy, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../../foundation/altzero_base/base/components/ui/dialog';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { bulkReportService, type DuplicatePeriod } from '../../services/bulkReportService';
import type { BvBill } from '../../types/billTypes';

// "Fix duplicates" on the Bulk Bill Run board. Lists every bill period that has
// more than one active bill, resolves the ones with an obvious answer in one go,
// and leaves the ones that need a person's choice listed with "Keep this one"
// on their board rows (migration 128).

type DuplicatePeriodsDialogProps = {
  /** The board's bills, to name each period's client and NMI. */
  bills: BvBill[];
  /** `changed`: bills were archived, reload the board. */
  onClose: (changed: boolean) => void;
};

type Phase = 'loading' | 'confirm' | 'working' | 'done' | 'error';

export const KIND_LABELS: Record<DuplicatePeriod['kind'], string> = {
  A: 'One bill has the invoice, the others are empty — keep the one with the invoice',
  B: 'The same invoice is on several bills — keep one',
  D: 'No bill has an invoice yet — keep one',
  C: 'The bills hold different invoices — choose with “Keep this one” on the board',
};

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('en-AU', { timeZone: 'UTC' });
}

export const DuplicatePeriodsDialog = ({ bills, onClose }: DuplicatePeriodsDialogProps) => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [plan, setPlan] = useState<DuplicatePeriod[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    void bulkReportService.planDuplicates().then((res) => {
      if (!mounted) return;
      if (res.success) { setPlan(res.data ?? []); setPhase('confirm'); }
      else { setError(res.error ?? 'Could not list duplicate periods'); setPhase('error'); }
    });
    return () => { mounted = false; };
  }, []);

  const byId = new Map(bills.map((b) => [b.id, b]));
  const obvious = plan.filter((p) => p.kind !== 'C');
  const toArchive = obvious.reduce((n, p) => n + p.billIds.length - 1, 0);
  const needChoice = plan.filter((p) => p.kind === 'C');
  const counts = (['A', 'B', 'D', 'C'] as const).map((k) => [k, plan.filter((p) => p.kind === k).length] as const);

  const fix = async () => {
    setPhase('working');
    setError(null);
    const res = await bulkReportService.fixObviousDuplicates();
    if (res.success) { setPlan(res.data ?? []); setPhase('done'); }
    else { setError(res.error ?? 'Could not fix the duplicates'); setPhase('error'); }
  };

  const resolvedCount = plan.filter((p) => p.resolved).length;
  const describe = (p: DuplicatePeriod) => {
    const b = byId.get(p.billIds[0]);
    return `${b?.site?.client?.name ?? 'Unknown client'} · ${b?.site?.nmi ?? '—'} · ${fmtDate(p.billFrom)} – ${fmtDate(p.billTo)}`;
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && phase !== 'working') onClose(phase === 'done'); }}>
      <DialogContent data-testid="duplicates-dialog" className="max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Copy className="h-4 w-4" /> Duplicate bill periods
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4 text-sm">
          {phase === 'loading' && (
            <p className="flex items-center gap-2 text-gray-600"><Loader2 className="h-4 w-4 animate-spin" /> Finding duplicate periods…</p>
          )}

          {error && (
            <p data-testid="duplicates-error" className="flex items-start gap-2 text-red-600"><AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {error}</p>
          )}

          {phase === 'confirm' && plan.length === 0 && (
            <p data-testid="duplicates-none" className="text-gray-700">No bill period has more than one active bill.</p>
          )}

          {(phase === 'confirm' || phase === 'working') && plan.length > 0 && (
            <>
              <p className="text-gray-700">
                <strong>{plan.length}</strong> bill period{plan.length === 1 ? ' has' : 's have'} more than one active bill.
                Each fix keeps one bill, archives the others as duplicates (they can be restored), and moves their
                inbox emails onto the kept bill. Bills that have had a report sent are never archived.
              </p>
              <ul data-testid="duplicates-counts" className="space-y-1.5">
                {counts.filter(([, n]) => n > 0).map(([k, n]) => (
                  <li key={k} data-kind={k} className="flex gap-2">
                    <span className="font-semibold w-8 text-right shrink-0">{n}</span>
                    <span className={k === 'C' ? 'text-amber-700' : 'text-gray-700'}>{KIND_LABELS[k]}</span>
                  </li>
                ))}
              </ul>
              {needChoice.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <p className="text-xs font-medium text-amber-900 mb-1">Need your choice (left as they are):</p>
                  <ul className="text-xs text-amber-900 space-y-0.5">
                    {needChoice.map((p) => <li key={p.billIds.join()}>{describe(p)}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}

          {phase === 'working' && (
            <p className="flex items-center gap-2 text-gray-700"><Loader2 className="h-4 w-4 animate-spin" /> Archiving duplicates…</p>
          )}

          {phase === 'done' && (
            <div data-testid="duplicates-done" className="space-y-2">
              <p className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-4 w-4" /> Fixed {resolvedCount} bill period{resolvedCount === 1 ? '' : 's'}.
              </p>
              {needChoice.length > 0 && (
                <p className="text-gray-700">
                  {needChoice.length} period{needChoice.length === 1 ? '' : 's'} with different invoices {needChoice.length === 1 ? 'is' : 'are'} still
                  flagged on the board — open each and use <strong>Keep this one</strong> on the bill to keep.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-3 border-t">
          {phase === 'done' || phase === 'error' || (phase === 'confirm' && obvious.length === 0) ? (
            <Button data-testid="duplicates-close" onClick={() => onClose(phase === 'done')}>Close</Button>
          ) : (
            <>
              <Button variant="outline" disabled={phase === 'working'} onClick={() => onClose(false)}>Cancel</Button>
              <Button data-testid="duplicates-fix" disabled={phase !== 'confirm'} onClick={() => void fix()}>
                Fix {obvious.length} period{obvious.length === 1 ? '' : 's'} (archive {toArchive} bill{toArchive === 1 ? '' : 's'})
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
